import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { createAccount, getAccounts, deleteAccount, updateAccount } from '../services/accountService';
import './Accounts.css';

const ACCOUNT_TYPES = ['Ahorro', 'Crédito', 'Tarjeta de Crédito'];

const Accounts = () => {
  const { currentUser } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'Ahorro',
    balance: '',
    creditLimit: ''
  });

  useEffect(() => {
    loadAccounts();
  }, [currentUser]);

  const loadAccounts = async () => {
    if (currentUser) {
      const accs = await getAccounts(currentUser.uid);
      setAccounts(accs);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (editingAccount) {
      await updateAccount(editingAccount.id, {
        ...formData,
        balance: parseFloat(formData.balance) || 0,
        creditLimit: parseFloat(formData.creditLimit) || 0
      });
    } else {
      await createAccount(currentUser.uid, {
        ...formData,
        balance: parseFloat(formData.balance) || 0,
        creditLimit: parseFloat(formData.creditLimit) || 0
      });
    }
    
    setFormData({ name: '', type: 'Ahorro', balance: '', creditLimit: '' });
    setShowForm(false);
    setEditingAccount(null);
    loadAccounts();
  };

  const handleEdit = (account) => {
    setEditingAccount(account);
    setFormData({
      name: account.name,
      type: account.type,
      balance: account.balance?.toString() || '',
      creditLimit: account.creditLimit?.toString() || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Eliminar esta cuenta? Las transacciones asociadas mantendrán el registro.')) {
      await deleteAccount(id);
      loadAccounts();
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingAccount(null);
    setFormData({ name: '', type: 'Ahorro', balance: '', creditLimit: '' });
  };

  return (
    <div className="accounts-page">
      <header>
        <h1>🏦 Cuentas</h1>
        <button onClick={() => setShowForm(!showForm)} className="add-btn">
          {showForm ? 'Cancelar' : '+ Nueva Cuenta'}
        </button>
      </header>

      {showForm && (
        <form onSubmit={handleSubmit} className="account-form">
          <h3>{editingAccount ? 'Editar Cuenta' : 'Nueva Cuenta'}</h3>
          
          <input
            type="text"
            placeholder="Nombre de la cuenta"
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            required
          />

          <select
            value={formData.type}
            onChange={(e) => setFormData({...formData, type: e.target.value})}
          >
            {ACCOUNT_TYPES.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>

          <input
            type="number"
            step="0.01"
            placeholder="Balance inicial"
            value={formData.balance}
            onChange={(e) => setFormData({...formData, balance: e.target.value})}
          />

          {formData.type === 'Tarjeta de Crédito' && (
            <input
              type="number"
              step="0.01"
              placeholder="Límite de crédito"
              value={formData.creditLimit}
              onChange={(e) => setFormData({...formData, creditLimit: e.target.value})}
            />
          )}

          <div className="form-buttons">
            <button type="submit" className="save-btn">
              {editingAccount ? 'Actualizar' : 'Guardar'}
            </button>
            <button type="button" onClick={handleCancel} className="cancel-btn">
              Cancelar
            </button>
          </div>
        </form>
      )}

      <div className="accounts-list">
        {accounts.length === 0 ? (
          <div className="empty-state">
            <p>No hay cuentas creadas</p>
            <p className="hint">Crea tu primera cuenta usando el botón "+ Nueva Cuenta"</p>
          </div>
        ) : (
          accounts.map(acc => (
            <div key={acc.id} className={`account-card ${acc.type.toLowerCase().replace(/\s/g, '-')}`}>
              <div className="account-header">
                <h3>{acc.name}</h3>
                <span className="account-type">{acc.type}</span>
              </div>
              <div className="account-balance">
                <span className="label">Balance:</span>
                <span className="amount">${acc.balance?.toFixed(2) || '0.00'}</span>
              </div>
              {acc.type === 'Tarjeta de Crédito' && (
                <div className="credit-limit">
                  <span className="label">Límite:</span>
                  <span className="amount">${acc.creditLimit?.toFixed(2) || '0.00'}</span>
                </div>
              )}
              <div className="account-actions">
                <button onClick={() => handleEdit(acc)} className="edit-btn">
                  ✏️ Editar
                </button>
                <button onClick={() => handleDelete(acc.id)} className="delete-btn">
                  🗑️ Eliminar
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <a href="/dashboard" className="back-link">← Volver al Dashboard</a>
    </div>
  );
};

export default Accounts;
